import type { DbPortalSkala, DbPortalSkalaEintrag } from '@gql/shared-types';
import { MERKMAL_TEXT } from '../daten/skala.js';
import { formatiereDatum } from '../daten/zeitreihe.js';

const STATUS_TEXT: Record<string, string> = {
  bewertet: 'bewertet',
  vorläufig: 'vorläufig',
  offen: 'noch offen',
};

export function SkalaPunkte({ eintrag, klein = false }: { eintrag: DbPortalSkalaEintrag; klein?: boolean }) {
  const wert = eintrag.wert ?? 0;
  const label =
    eintrag.wert === null
      ? 'Vergleichbarkeit noch offen'
      : `Vergleichbarkeit ${eintrag.wert} von 5${eintrag.status === 'vorläufig' ? ', vorläufig' : ''}`;
  return (
    <span className={`skala-punkte${klein ? ' skala-punkte-klein' : ''}`} role="img" aria-label={label} title={label}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={`skala-kaestchen${i <= wert ? ' ist-voll' : ''}${eintrag.status === 'vorläufig' ? ' ist-vorlaeufig' : ''}`}
        />
      ))}
    </span>
  );
}

export function SkalaBlock({ eintrag, katalog }: { eintrag: DbPortalSkalaEintrag; katalog: DbPortalSkala['merkmale'] }) {
  const merkmale = eintrag.merkmale ? katalog.filter((m) => eintrag.merkmale?.[m.id]) : [];
  return (
    <div className="skala-block">
      <div className="skala-kopf">
        {eintrag.wert !== null ? (
          <>
            <SkalaPunkte eintrag={eintrag} />
            <span className="skala-wert">{eintrag.referenz ? 'Referenzgebiet' : `${eintrag.wert} von 5`}</span>
          </>
        ) : (
          <span className="skala-wert">Noch nicht bewertet</span>
        )}
        <span className={`marke marke-${eintrag.status === 'vorläufig' ? 'vorlaeufig' : eintrag.status}`}>
          {STATUS_TEXT[eintrag.status]}
        </span>
      </div>
      {eintrag.begruendung && <p>{eintrag.begruendung}</p>}
      {eintrag.status === 'offen' && (
        <p className="hinweis-klein">
          Die Leistungsmerkmale dieses Gebiets werden derzeit mit Berlin abgeglichen. Das Ergebnis erscheint hier, sobald es
          vorliegt.
        </p>
      )}
      {eintrag.quelle && (
        <p className="hinweis-klein">
          Grundlage: {eintrag.quelle}
          {eintrag.aktualisiert_am && ` · Stand ${formatiereDatum(eintrag.aktualisiert_am)}`}
          {eintrag.wert_herkunft === 'berechnet' && ' · Wert aus den Leistungsmerkmalen berechnet'}
        </p>
      )}
      {merkmale.length > 0 && (
        <details className="merkmale">
          <summary>Leistungsmerkmale im Einzelnen ({merkmale.length})</summary>
          <table className="tabelle">
            <thead>
              <tr>
                <th scope="col">Merkmal</th>
                <th scope="col">Ergebnis</th>
              </tr>
            </thead>
            <tbody>
              {merkmale.map((m) => {
                const status = eintrag.merkmale![m.id]!;
                const t = MERKMAL_TEXT[status]!;
                return (
                  <tr key={m.id}>
                    <td>
                      <span className="merkmal-id">{m.id}</span> {m.name}
                    </td>
                    <td className={`merkmal-status merkmal-${status === 'entfällt' ? 'entfaellt' : status}`}>
                      <span aria-hidden="true">{t.zeichen}</span> {t.text}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}
